import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import { UploadStreamResponse } from '@/types/document';
import { VaultDocument } from '@/types/vault';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useAssignDocumentToFolder } from '@/api/endpoints/document/useAssignDocumentToFolder';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { useGetVaultDocuments } from '@/api/endpoints/vault/useGetVaultDocuments';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';
import { getResolvedRoutePath } from '@/utils/router';

import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';
import DocumentViewModal from '@/components/document/DocumentViewModal';
import FolderView from '@/components/document-folder/FolderView';
import VaultDocumentTagModal from '@/components/vault/VaultDocumentTagModal';

// Helper function to get status message

interface FilterState {
  category: string;
  tags: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

interface VaultPageParams {
  folderId?: string;
}

const VaultPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const location = useLocation();
  const history = useHistory();
  const { folderId } = useParams<VaultPageParams>();
  const pendingCategoryIdRef = useRef<number | null>(null);
  const dialogOpenedRef = useRef<boolean>(false);
  const documentUploadButtonRef = useRef<DocumentUploadButtonAPI>(null);
  const queryClient = useQueryClient();

  // --- State Hooks ---
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    category: '',
    tags: [],
    dateRange: {
      start: null,
      end: null,
    },
  });
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId] = useState<number | null>(null);
  const [, setPendingUploads] = useState<Set<string>>(new Set());

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(
    folderId ? Number(folderId) : null
  );

  // --- API Hooks ---
  const { refetch, isLoading } = useGetVaultDocuments(
    {
      property_location_id: selectedPropertyLocation?.id,
    },
    !!session?.access_token && !!selectedPropertyLocation?.id
  );

  const { data: categories = [] } = useGetDocumentCategories();

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const assignDocumentToFolderMutation = useAssignDocumentToFolder();

  // Single useDocumentUploadV2 instance for streaming upload and processing
  const { uploadFile: uploadWithStreaming, isUploading } = useDocumentUploadV2({
    onProgress: (response: UploadStreamResponse) => {
      // If status is completed but no document_id, trigger a refetch to get the document
      if (response.status === 'completed' && !response.document_id) {
        setPendingUploads(new Set());
        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
        return;
      }

      // Handle progress updates
      if (response.document_id) {
        // Clear any pending uploads since we now have a document ID
        setPendingUploads(new Set());
        // Refetch to get the new document
        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
      } else {
        // Update pending uploads with progress
      }
    },
    onComplete: (response: UploadStreamResponse) => {
      if (response.document_id) {
        // Trigger folder assignment after successful upload
        assignDocumentToFolderMutation.mutate(response.document_id);

        refetch();
        // Invalidate folder queries to refresh the folder list
        queryClient.invalidateQueries({ queryKey: ['folders'] });
      } else {
        // No document_id in completion, clean up pending uploads and refetch
        setPendingUploads(new Set());
        setTimeout(() => {
          refetch();
          // Invalidate folder queries to refresh the folder list
          queryClient.invalidateQueries({ queryKey: ['folders'] });
        }, 1000);
      }
    },
    onError: (errorMessage: string) => {
      // Improved error handling for timeouts
      if (errorMessage.includes('Upload timed out') || errorMessage.includes('502')) {
        enqueueSnackbar(
          'Upload is taking longer than expected. The file has been uploaded and is being processed in the background. Please refresh the page in a few minutes to see the document.',
          {
            variant: 'warning',
            persist: true,
            action: (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  refetch();
                }}
              >
                Refresh Now
              </Button>
            ),
          }
        );
      } else {
        enqueueSnackbar(`Upload failed: ${errorMessage}`, { variant: 'error' });
      }
      // Clean up any pending uploads
      setPendingUploads(new Set());
    },
  });

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleTagModalClose = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
  };

  const handleTagModalSuccess = () => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
    refetch();
  };

  const handleFilterClose = () => {
    setFilterDialogOpen(false);
  };

  const handleFilterApply = (newFilters: FilterState) => {
    setFilters(newFilters);
    setFilterDialogOpen(false);
  };

  const handleFilterReset = () => {
    setFilters({
      category: '',
      tags: [],
      dateRange: {
        start: null,
        end: null,
      },
    });
  };

  const handleUploadClick = useCallback(() => {
    documentUploadButtonRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!selectedPropertyLocation?.id) {
        enqueueSnackbar('Please select a property location first', { variant: 'error' });
        return;
      }

      // Create a temporary identifier for this upload
      const uploadId = `upload_${Date.now()}_${file.name}`;

      // Create temporary upload entry to show in the list immediately
      setPendingUploads(new Set([uploadId]));

      uploadWithStreaming({
        file,
        location_ids: [selectedPropertyLocation.id],
        folder_id: currentFolderId,
        tags: {
          property_location_ids: [selectedPropertyLocation.id],
          building_ids: [],
          unit_ids: [],
          tenant_ids: [],
          property_manager_ids: [],
        },
      });
    },
    [selectedPropertyLocation?.id, uploadWithStreaming, currentFolderId, setPendingUploads]
  );

  // Check for openDocumentUpload query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openDocumentUpload') === 'true' && !dialogOpenedRef.current) {
      // Mark that we've opened the dialog for this session
      dialogOpenedRef.current = true;

      // Store defaultCategoryId if present
      const defaultCategoryId = searchParams.get('defaultCategoryId');
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
      }

      // Clear URL parameters immediately
      const newUrl = new URL(window.location.href);
      newUrl.search = '';
      history.replace(newUrl.pathname);

      // Open the upload dialog
      handleUploadClick();
    }
  }, [location.search, handleUploadClick, history]);

  // Refetch documents when selected property location changes
  useEffect(() => {
    if (selectedPropertyLocation?.id && session?.access_token) {
      refetch();
    }
  }, [selectedPropertyLocation?.id, session?.access_token, refetch]);

  // Handle folder navigation
  const handleFolderChange = useCallback(
    (newFolderId: number | null) => {
      setCurrentFolderId(newFolderId);
      if (newFolderId) {
        history.push(
          getResolvedRoutePath(PMPRoutes.vaultFolder, {
            folderId: String(newFolderId),
          })
        );
      } else {
        history.push(PMPRoutes.vault);
      }
    },
    [history]
  );

  // Sync URL folder ID with state
  useEffect(() => {
    if (folderId) {
      setCurrentFolderId(Number(folderId));
    } else {
      setCurrentFolderId(null);
    }
  }, [folderId]);

  // Show message if no location is selected
  if (!selectedPropertyLocation?.id) {
    return (
      <MuiPageWrapper>
        <Box>
          <Stack direction="column" spacing={3}>
            <DashboardHeader hasInnerHtml title="Documents: <em>Active</em>" />
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Please select a property location to view documents.
            </Typography>
          </Stack>
        </Box>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Box>
        <Stack direction="column" spacing={3}>
          <Box
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              display: 'flex',
              justifyContent: { sm: 'space-between' },
              width: '100%',
            }}
          >
            <DashboardHeader hasInnerHtml title="Documents: <em>Vault</em>" />
            <LoadingButton
              variant="contained"
              startIcon={<PlusIcon />}
              onClick={handleUploadClick}
              loading={isUploading}
              loadingPosition="start"
              disabled={isLoading || !selectedPropertyLocation?.id}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                alignSelf: isMobile ? 'stretch' : 'auto',
                py: isMobile ? 1 : 1.5,
                borderRadius: 1.5,
              }}
            >
              Upload Document
            </LoadingButton>
          </Box>

          <Typography variant="body1">
            View and manage documents for {selectedPropertyLocation.name}.
          </Typography>

          <DocumentUploadButton
            ref={documentUploadButtonRef}
            isFileUploading={isUploading}
            onFileSelect={handleFileSelect}
            sx={{ display: 'none' }}
          />

          <FolderView
            currentFolderId={currentFolderId}
            propertyLocationId={selectedPropertyLocation?.id}
            onFolderChange={handleFolderChange}
          />

          {/*
          // --- Commented out: Old Table-based Document List ---
          <Card sx={{ bgcolor: 'common.white' }}>
            <DropZone onFileDrop={handleFileSelect} isUploading={isUploading}>
              ...
            </DropZone>
          </Card>
          */}
        </Stack>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 500 },
          },
        }}
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {isTagModalOpen && selectedDocument && (
        <VaultDocumentTagModal
          document={selectedDocument}
          onClose={handleTagModalClose}
          onSuccess={handleTagModalSuccess}
        />
      )}

      <Dialog
        open={filterDialogOpen}
        onClose={handleFilterClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', sm: 400 },
            maxWidth: { xs: '90%', sm: 600 },
          },
        }}
      >
        <DialogTitle>Filter Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Category
              </Typography>
              <Select
                fullWidth
                value={filters.category}
                onChange={e => setFilters(prev => ({ ...prev, category: e.target.value }))}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[400],
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary[600],
                    borderWidth: '1px',
                  },
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Date Added
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={filters.dateRange.start?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        start: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={filters.dateRange.end?.toISOString().split('T')[0] || ''}
                  onChange={e =>
                    setFilters(prev => ({
                      ...prev,
                      dateRange: {
                        ...prev.dateRange,
                        end: e.target.value ? new Date(e.target.value) : null,
                      },
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[400],
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.secondary[600],
                      borderWidth: '1px',
                    },
                  }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFilterReset}>Reset</Button>
          <Button onClick={handleFilterClose}>Cancel</Button>
          <Button onClick={() => handleFilterApply(filters)} variant="contained">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {previewDocumentId && (
        <DocumentViewModal
          id={previewDocumentId}
          categoryId={previewDocumentCategoryId ?? undefined}
          onClose={() => setPreviewDocumentId(null)}
        />
      )}
    </MuiPageWrapper>
  );
};

export default VaultPage;
